import type { SegmentDTO } from '@next-space/fe-api-idl';
import { BlockType } from '@next-space/fe-api-idl';
import { getTableCellEditorKey } from 'src/editor/editor/plugin/table/helper';
import { getEditorModelByEditorKey } from 'src/editor/editor/uikit/editable-models';
import { convertSegmentsToContent } from 'src/editor/utils/segments';
import { addBlock } from 'src/redux/managers/block/add';
import { updateBlock } from 'src/redux/managers/block/update';
import { simpleTableActions } from 'src/redux/reducers/simple-table';
import { uiActions } from 'src/redux/reducers/ui';
import { cache, dispatch } from 'src/redux/store';
import { blurDocument } from 'src/utils/dom';
import { v4 as uuidV4 } from 'uuid';
import type { PasteHook, RecordCells } from '../types';

export const pasteTableCells = ({ transaction }: PasteHook, records: RecordCells[]) => {
  const { selectedCells } = cache.ui;

  const cell = selectedCells[0];
  if (!cell) return;

  const { viewId, syncId } = cell;
  const table = cache.blocks[viewId];
  if (!table) return;

  const { subNodes, data } = table;
  const columns = data.format?.tableBlockColumnOrder ?? [];

  const rowIndexes: Record<string, number> = {};
  const columnIndexes: Record<string, number> = {};

  selectedCells.forEach(({ recordId, propertyId }) => {
    if (!rowIndexes[recordId]) {
      const rowIndex = subNodes.indexOf(recordId);
      rowIndexes[recordId] = rowIndex;
    }

    if (!columnIndexes[propertyId]) {
      const columnIndex = columns.indexOf(propertyId);
      columnIndexes[propertyId] = columnIndex;
    }
  });

  // 找到左上角的选区
  const rowIndex = Math.min(...Object.values(rowIndexes));
  const columnIndex = Math.min(...Object.values(columnIndexes));

  const onlyOneCell = records.length === 1 && !!records[0] && records[0].cells.length === 1;

  transaction(() => {
    const newRows: string[] = [];
    const newColumns: string[] = [];

    const pasteColumnLength = records[0]?.cells.length ?? 0;
    const diff = columnIndex + pasteColumnLength - columns.length;

    if (diff > 0) {
      for (let i = diff; i > 0; i--) {
        // 创建多出来的列 id
        newColumns.push(uuidV4());
      }
    }

    records.forEach((record, i) => {
      const { cells } = record;

      const rowId = subNodes[rowIndex + i];

      const getCollectionProperties = (data: Record<string, SegmentDTO[]> = {}) => {
        return cells.reduce(
          (o, segments, index) => {
            const propertyId = columns[columnIndex + index];
            if (propertyId) {
              o[propertyId] = segments;
            } else {
              const pid = newColumns[columnIndex + index - columns.length];
              if (pid) {
                o[pid] = segments;
              }
            }
            return o;
          },
          { ...data }
        );
      };

      if (rowId) {
        const row = cache.blocks[rowId];
        const collectionProperties = getCollectionProperties(row?.data.collectionProperties);

        if (onlyOneCell && cache.simpleTable.focus) {
          const content = convertSegmentsToContent(cells[0]);
          const focusEditorKey = getTableCellEditorKey(viewId, rowIndex, columnIndex);
          if (focusEditorKey) {
            const editorModel = getEditorModelByEditorKey(focusEditorKey);
            if (editorModel) {
              editorModel.performChange((ctx) => {
                ctx.delete();
                ctx.insert(content);
              });
            }
          }
        } else {
          updateBlock(rowId, { data: { collectionProperties } });
        }
      } else {
        const collectionProperties = getCollectionProperties();
        newRows.push(
          addBlock(
            { type: BlockType.TABLE_ROW, data: { collectionProperties } },
            { parentId: viewId }
          )
        );
      }
    });

    if (onlyOneCell) return;

    const finalRows = [...subNodes, ...newRows];
    const finalColumns = [...columns, ...newColumns];

    if (newRows.length) {
      updateBlock(viewId, { subNodes: finalRows });
    }

    if (newColumns.length) {
      updateBlock(viewId, {
        data: {
          format: {
            ...data.format,
            tableBlockColumnOrder: finalColumns,
          },
        },
      });
    }

    const selectedCells = [];
    for (const recordId of finalRows.slice(rowIndex, rowIndex + records.length)) {
      for (const propertyId of finalColumns.slice(columnIndex, columnIndex + pasteColumnLength)) {
        selectedCells.push({ recordId, propertyId, viewId, syncId });
      }
    }
    dispatch(uiActions.update({ selectedCells }));
    dispatch(simpleTableActions.update({ focus: false }));
    blurDocument();
  });
};
